import { useCallback, useEffect, useState } from 'react';
import type { AuditLog } from '../models/AuditLog';
import { RENEWALS_ALERT_DAYS } from '../config/constants';
import { auditLogService } from '../services/dataverse/auditLogService';
import { clientService } from '../services/dataverse/clientService';
import { subscriptionService } from '../services/dataverse/subscriptionService';
import { apiClient } from '../services/api/apiClient';
import { formatDate } from '../utils/date';

export interface DashboardKpi {
  label: string;
  value: string;
  helperText: string;
}

export interface DashboardAlert {
  title: string;
  description: string;
  intent: 'warning' | 'error' | 'info' | 'success';
  sortDate?: string;
}

export interface RevenuePoint {
  label: string;
  offers: number;
  orders: number;
}

interface DashboardStats {
  kpis: {
    totalClients: number;
    totalOffers: number;
    totalOffersPipeline: number;
    renewalsNext30Days: number;
    totalOrdersCount: number;
    totalSubscriptionsActive: number;
  };
  revenueByMonth: { label: string; offers: number; orders: number }[];
}

export const useDashboard = () => {
  const [state, setState] = useState<{
    kpis: DashboardKpi[];
    recentActivity: AuditLog[];
    revenueSeries: RevenuePoint[];
    alerts: DashboardAlert[];
    loading: boolean;
    error: string | null;
  }>({
    kpis: [],
    recentActivity: [],
    revenueSeries: [],
    alerts: [],
    loading: true,
    error: null,
  });

  const reload = useCallback(async () => {
    setState((current) => ({ ...current, loading: true, error: null }));
    try {
      // Fetch dashboard stats from backend if configured, otherwise fall back to individual calls
      let kpis: DashboardKpi[];
      let revenueSeries: RevenuePoint[];
      let alerts: DashboardAlert[];

      if (apiClient.isConfigured()) {
        const [stats, recentActivity, clients, expiringSubscriptions] = await Promise.all([
          apiClient.get<DashboardStats>('/api/dashboard/stats'),
          auditLogService.list(),
          clientService.list(),
          subscriptionService.getExpiringSoon(RENEWALS_ALERT_DAYS),
        ]);

        kpis = [
          { label: 'Clients', value: `${stats.kpis.totalClients}`, helperText: `${clients.filter((c) => c.cr_crm_syncerror).length} sync issues` },
          { label: 'Offers', value: `${stats.kpis.totalOffers}`, helperText: `Pipeline: ${stats.kpis.totalOffersPipeline.toFixed(0)} TND` },
          { label: 'Renewals', value: `${stats.kpis.renewalsNext30Days}`, helperText: `Next ${RENEWALS_ALERT_DAYS} days` },
          { label: 'Orders', value: `${stats.kpis.totalOrdersCount}`, helperText: `${stats.kpis.totalSubscriptionsActive} active subs` },
        ];

        revenueSeries = stats.revenueByMonth.map((m) => {
          // Format "2025-03" → "Mar 25"
          const [y, mo] = m.label.split('-');
          const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
          const shortLabel = `${monthNames[parseInt(mo!, 10) - 1]} ${y!.slice(2)}`;
          return { label: shortLabel, offers: m.offers, orders: m.orders };
        });

        alerts = [
          ...clients
            .filter((item) => item.cr_crm_syncerror)
            .map((item) => ({
              title: item.cr_crm_companyname,
              description: item.cr_crm_syncerror ?? 'Synchronization issue detected.',
              intent: 'error' as const,
            })),
          ...expiringSubscriptions.map((item) => ({
            title: item.cr_crm_skuname,
            description: `${item.cr_crm_client?.cr_crm_companyname ?? 'Unknown client'} renews on ${formatDate(item.cr_crm_renewaldate ?? item.cr_crm_enddate)}.`,
            intent: 'warning' as const,
            sortDate: item.cr_crm_renewaldate ?? item.cr_crm_enddate ?? '9999',
          })),
        ];

        setState({
          kpis,
          recentActivity: recentActivity.items.slice(0, 10),
          revenueSeries,
          alerts,
          loading: false,
          error: null,
        });
      } else {
        // Fallback: individual calls (mock/offline mode)
        const [clients, offers, expiringSubscriptions, recentActivity] = await Promise.all([
          clientService.list(),
          (await import('../services/dataverse/offerService')).offerService.list(),
          subscriptionService.getExpiringSoon(RENEWALS_ALERT_DAYS),
          auditLogService.list(),
        ]);

        const totalRevenue = offers.reduce((sum, item) => sum + (item.cr_crm_totaltnd ?? 0), 0);
        kpis = [
          { label: 'Clients', value: `${clients.length}`, helperText: `${clients.filter((item) => item.cr_crm_syncerror).length} sync issues` },
          { label: 'Offers', value: `${offers.length}`, helperText: `${offers.filter((item) => item.cr_crm_status === 1).length} drafts` },
          { label: 'Renewals', value: `${expiringSubscriptions.length}`, helperText: `Next ${RENEWALS_ALERT_DAYS} days` },
          { label: 'Revenue', value: `${totalRevenue.toFixed(3)} TND`, helperText: 'Current pipeline' },
        ];

        revenueSeries = offers.map((item) => ({ label: item.cr_crm_reference, offers: item.cr_crm_totaltnd ?? 0, orders: 0 }));

        alerts = [
          ...clients
            .filter((item) => item.cr_crm_syncerror)
            .map((item) => ({
              title: item.cr_crm_companyname,
              description: item.cr_crm_syncerror ?? 'Synchronization issue detected.',
              intent: 'error' as const,
            })),
          ...expiringSubscriptions.map((item) => ({
            title: item.cr_crm_skuname,
            description: `${item.cr_crm_client?.cr_crm_companyname ?? 'Unknown client'} renews on ${formatDate(item.cr_crm_renewaldate ?? item.cr_crm_enddate)}.`,
            intent: 'warning' as const,
            sortDate: item.cr_crm_renewaldate ?? item.cr_crm_enddate ?? '9999',
          })),
        ];

        setState({
          kpis,
          recentActivity: recentActivity.items.slice(0, 10),
          revenueSeries,
          alerts,
          loading: false,
          error: null,
        });
      }
    } catch (err) {
      setState((current) => ({
        ...current,
        loading: false,
        error: err instanceof Error ? err.message : 'Unable to load dashboard.',
      }));
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { ...state, reload };
};
